import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TpRectangleCard from "./TpRectangleCard";
import axios from "axios";
import { PiFireLight } from "react-icons/pi";
import { PiNewspaperClipping } from "react-icons/pi";
import { mapApiResponseToContentItem } from "../../../utils/contentMappers";
import backIcon from "../../../assets/images/arrow-left.svg";
import useStore from "../../../store/useStore";
import {
  bookmarkContent,
  getContentCounts,
  getLikeBookmarkedContent,
  likeContent,
} from "../service";
import { getDecodedToken, getLocalStorage } from "../../../utils/localStorage";
import { AUTH_TOKEN } from "../../../utils/constants";
import { SOLR_API } from "../../../envConstants";

const API_URL = `${SOLR_API}/select`;

interface ContentItem {
  id: number;
  contentTitle: string;
  contentType: string;
  contentDescription: string;
  uploadThumbnailUrl: string;
  coverPhotoUrl: string;
  duration: string;
  isBookmark: boolean;
  viewCount: number;
  like_count: number;
  shareCount: number;
  docId: number;
  previewDeepLink: string;
  contentTypeId: any;
  publishedDate: string;
  deepLinkCode: string;
}

interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

interface ContentListSectionProps {
  title?: string;
  items?: ContentItem[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  iconComponent?: React.ReactNode;
  onBackClick?: () => void;

  // New props for self-fetching capability
  contentType?: {
    id?: number;
    name?: string;
  };
  genre?: {
    id?: number;
    name?: string;
  };
  trending?: boolean;
  fetchExternally?: boolean; // If true, use provided items prop instead of fetching
}

const ContentListSection: React.FC<ContentListSectionProps> = ({
  title: propTitle,
  items: propItems = [],
  loading: propLoading = false,
  onLoadMore: propOnLoadMore,
  hasMore: propHasMore = false,
  iconComponent: propIconComponent,
  onBackClick,
  contentType,
  genre,
  trending = false,
  fetchExternally = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [displayItems, setDisplayItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(
    fetchExternally ? propLoading : true
  );
  const [hasMore, setHasMore] = useState<boolean>(
    fetchExternally ? propHasMore : true
  );
  const [page, setPage] = useState<number>(1);
  const PAGE_SIZE = 5;
  const loaderRef = useRef<HTMLDivElement>(null);
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});

  const {
    tatvaPediaToken,
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
    selectedTatvaPediaFilters,
  } = useStore();

  // Add a ref to track pending operations
  const pendingOperations = useRef<{ [key: string]: boolean }>({});

  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);
  // Add effect to sync with global state
  useEffect(() => {
    if (!tatvaPediaBookmarks?.likedList || pendingOperations.current["like"])
      return;

    const likedIds = tatvaPediaBookmarks.likedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalLikes(likedIds);
  }, [tatvaPediaBookmarks?.likedList]);

  // Add effect to sync with global bookmarks state
  useEffect(() => {
    if (
      !tatvaPediaBookmarks?.bookmarkedList ||
      pendingOperations.current["bookmark"]
    )
      return;

    const bookmarkedIds = tatvaPediaBookmarks.bookmarkedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalBookmarks(bookmarkedIds);
  }, [tatvaPediaBookmarks?.bookmarkedList]);

  // Add effect to fetch initial liked and bookmarked content
  useEffect(() => {
    const fetchInitialState = async () => {
      if (!tatvaPediaToken) return;

      try {
        // Fetch both liked and bookmarked content
        const [likedResponse, bookmarkedResponse] = await Promise.all([
          getLikeBookmarkedContent("liked", tatvaPediaToken),
          getLikeBookmarkedContent("bookmarked", tatvaPediaToken),
        ]);

        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
          const likedIds = likedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalLikes(likedIds);
        }

        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
          const bookmarkedIds = bookmarkedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalBookmarks(bookmarkedIds);
        }
      } catch (error) {
        console.error("Error fetching initial state:", error);
      }
    };

    fetchInitialState();
  }, [tatvaPediaToken]);

  // Fetch initial content when component mounts
  useEffect(() => {
    const fetchInitialContent = async () => {
      try {
        setLoading(true);

        // Fetch initial content counts
        if (displayItems.length && getToken()) {
          const contents = displayItems.map((doc: any) => ({
            contentId: doc.docId,
            contentTypeId: doc.contentTypeId,
          }));

          const countsResponse = await getContentCounts({
            contents,
            token: getToken(),
            userId: doctorDetails?.user?.id,
          });

          const countsMap = countsResponse.body.reduce(
            (acc: any, item: any) => {
              acc[`${item.docId}_${item.docTypeId}`] = {
                like_count: item.likeCount,
                isExternalSharable: item.isExternalSharable,
                uploadThumbnailUrl: item.uploadThumbnailUrl,
                previewUrl: item.previewUrl,
                readTime: item.readTime,
                duration: item.duration,
              };
              return acc;
            },
            {}
          );

          setContentCounts(countsMap);
        }
      } catch (error) {
        console.error("Error fetching initial content:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialContent();
  }, [displayItems]); // Add displayItems as dependency

  // Helper function to get token
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  // Keep the existing useEffect for content counts during updates
  useEffect(() => {
    const fetchContentCounts = async () => {
      const authToken = getToken();
      if (!displayItems.length || !authToken) return;

      try {
        const contents = displayItems.map((doc: any) => ({
          contentId: doc.docId,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: authToken,
          userId: doctorDetails?.user?.id,
        });

        const countsMap = countsResponse.body.reduce((acc: any, item: any) => {
          acc[`${item.docId}_${item.docTypeId}`] = {
            like_count: item.likeCount,
            isExternalSharable: item.isExternalSharable,
            uploadThumbnailUrl: item.uploadThumbnailUrl,
            previewUrl: item.previewUrl,
            readTime: item.readTime,
            duration: item.duration,
          };
          return acc;
        }, {});

        setContentCounts(countsMap);
      } catch (error) {
        console.error("Error fetching content counts:", error);
      }
    };

    fetchContentCounts();
  }, [displayItems, getToken()]);

  // Update handleLike function
  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["like"] = true;

      // Optimistically update UI
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newLikes;
      });

      // Update content counts optimistically
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        // Update global state
        updateTatvaPediaLike(content, !isLiked);

        // Fetch fresh data
        const updatedLikedContent = await getLikeBookmarkedContent(
          "liked",
          token
        );
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);
        }
      } else {
        // Revert on failure
        setLocalLikes((prev) =>
          isLiked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert on error
      setLocalLikes((prev) =>
        isLiked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setTimeout(() => {
        setIsActionLoading(false);
        pendingOperations.current["like"] = false;
      }, 1500);
    }
  };

  // Update handleBookmark function
  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["bookmark"] = true;

      // Optimistically update local state
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (response) {
        // Update global state
        await updateTatvaPediaBookmark(content, !isBookmarked);

        // Fetch fresh bookmark data
        const updatedBookmarkedContent = await getLikeBookmarkedContent(
          "bookmarked",
          token
        );
        if (updatedBookmarkedContent) {
          setTatvaPediaBookmarkedList(updatedBookmarkedContent);
        }
      } else {
        // Revert on failure
        setLocalBookmarks((prev) =>
          isBookmarked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );
      }
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert on error
      setLocalBookmarks((prev) =>
        isBookmarked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setTimeout(() => {
        setIsActionLoading(false);
        pendingOperations.current["bookmark"] = false;
      }, 1500);
    }
  };

  const isItemLiked = (item: ContentItem) => {
    if (!item?.docId) return false;
    return (
      localLikes.includes(item.docId) ||
      tatvaPediaBookmarks?.likedList?.some(
        (likedItem: any) => likedItem.contentId === item.docId
      ) ||
      false
    );
  };

  const isItemBookmarked = (item: ContentItem) => {
    if (!item?.docId) return false;
    return (
      localBookmarks.includes(item.docId) ||
      tatvaPediaBookmarks?.bookmarkedList?.some(
        (bookmarkedItem: any) => bookmarkedItem.contentId === item.docId
      ) ||
      false
    );
  };

  // Determine title and icon based on props or query params
  const getContentDetails = useCallback(() => {
    // Check URL parameters first
    const params = new URLSearchParams(location.search);
    const contentTypeParam = decodeURIComponent(
      params.get("contentType") || ""
    );
    const typeParam = params.get("type");
    const typeIdParam = params.get("contentTypeId");
    const genreParam = params.get("genre");
    const genreIdParam = params.get("genreId");
    const trendingParam = params.get("trending");

    let title = propTitle;
    let iconComponent = propIconComponent;

    // Set title and icon based on URL parameters or props
    if (trendingParam === "true" || trending) {
      title = "Trending";
      iconComponent = <PiFireLight fill="#A461D8" className="w-6 h-6" />;
    } else if (typeParam || contentType?.name) {
      title = typeParam || contentType?.name;
      iconComponent = (
        <PiNewspaperClipping fill="#A461D8" className="w-6 h-6" />
      );
    } else if (genreParam || genre?.name) {
      title = genreParam || genre?.name;
      iconComponent = (
        <PiNewspaperClipping fill="#A461D8" className="w-6 h-6" />
      );
    }

    return {
      headerTitle: contentTypeParam
        ? contentTypeParam.charAt(0).toUpperCase() + contentTypeParam.slice(1)
        : contentTypeParam,
      title,
      iconComponent,
      contentTypeId: Number(typeIdParam) || contentType?.id,
      genreId: Number(genreIdParam) || genre?.id,
      isTrending: trendingParam === "true" || trending,
    };
  }, [
    location.search,
    propTitle,
    propIconComponent,
    contentType,
    genre,
    trending,
  ]);

  const {
    title,
    iconComponent,
    contentTypeId,
    genreId,
    isTrending,
    headerTitle,
  } = getContentDetails();

  // Function to fetch content
  const fetchContent = useCallback(
    async (pageNumber: number) => {
      if (fetchExternally) return; // Skip if we're using externally provided items

      setLoading(true);

      try {
        let payload = {
          params: {
            q: "*:*",
            fq: ["status:489", "contentCountryId:1"],
            sort: "publishedDate desc",
            start: (pageNumber - 1) * PAGE_SIZE,
            rows: PAGE_SIZE,
          },
        };

        // Add content type filter if specified
        if (contentTypeId) {
          if (Array.isArray(contentTypeId)) {
            payload.params.fq.push(
              `contentTypeId:(${contentTypeId.join(" OR ")})`
            );
          } else {
            payload.params.fq.push(`contentTypeId:${contentTypeId}`);
          }
        }

        // Add genre filter if specified
        if (genreId) {
          payload.params.fq.push(`contentGenreId:${genreId}`);
        }

        if (selectedTatvaPediaFilters?.Speciality?.length > 0) {
          payload.params.fq.push(
            `speciality_id:(${selectedTatvaPediaFilters?.Speciality?.map(
              (item: any) => item.id
            ).join(" OR ")})`
          );
        }

        if (selectedTatvaPediaFilters?.Continuum?.length > 0) {
          payload.params.fq.push(
            `contentContinuumId:(${selectedTatvaPediaFilters?.Continuum?.map(
              (item: any) => item.id
            ).join(" OR ")})`
          );
        }

        if (selectedTatvaPediaFilters?.Interestpreference?.length > 0) {
          payload.params.fq.push(
            `topic_id:(${selectedTatvaPediaFilters?.Interestpreference?.map(
              (item: any) => item.id
            ).join(" OR ")})`
          );
        }

        // If trending, add trending filter
        if (isTrending) {
          payload.params.fq.push("is_trending:true");
          payload.params.fq.push("trending_status:(true)");
        }

        const response = await axios.post(API_URL, payload, {
          headers: {
            authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
            accept: "application/json",
          },
        });

        if (response.data?.response?.docs) {
          const newItems = response.data.response.docs.map(
            mapApiResponseToContentItem
          );

          // For first page, replace items. For subsequent pages, append
          if (pageNumber === 1) {
            setDisplayItems(newItems);
          } else {
            setDisplayItems((prevItems) => [...prevItems, ...newItems]);
          }

          // Check if there are more items to load
          const totalItems = response.data.response.numFound || 0;
          setHasMore(pageNumber * PAGE_SIZE < totalItems);
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setLoading(false);
      }
    },
    [contentTypeId, genreId, isTrending, fetchExternally]
  );

  // Update display items when propItems changes (if using external items)
  useEffect(() => {
    if (fetchExternally) {
      setDisplayItems(propItems);
    }
  }, [propItems, fetchExternally]);

  // Initial fetch or when parameters change
  useEffect(() => {
    if (!fetchExternally) {
      setPage(1); // Reset page when parameters change
      fetchContent(1);
    }
  }, [fetchContent, fetchExternally]);

  // Intersection observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);

  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore) {
            if (fetchExternally && propOnLoadMore) {
              propOnLoadMore();
            } else if (!fetchExternally) {
              setPage((prevPage) => prevPage + 1);
              fetchContent(page + 1);
            }
          }
        },
        { threshold: 0.5 }
      );

      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, hasMore, fetchExternally, propOnLoadMore, fetchContent, page]
  );

  // Handle back button click
  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      // Get URL parameters to determine where to navigate back to
      const params = new URLSearchParams(location.search);
      const contentTypeParam = encodeURIComponent(
        params.get("contentType") || ""
      );

      // Navigate based on where we came from:
      // 1. If trending content, navigate back to main page with trending content type
      // 2. If specific content type, navigate back with that content type
      // 3. If genre content, navigate back with the content type
      if (contentTypeParam) {
        navigate(`/tatvapedia?activeTab=${contentTypeParam.toLowerCase()}`);
      } else if (contentTypeParam) {
        navigate(`/tatvapedia?activeTab=${contentTypeParam.toLowerCase()}`);
      } else {
        // Default fallback if we can't determine the source
        navigate(-1);
      }
    }
  };

  // Render loading skeletons
  const renderSkeletons = () => {
    return Array.from({ length: 2 }).map((_, index) => (
      <div key={`skeleton-${index}`} className="animate-pulse">
        <div className="flex gap-4 h-40 p-4 rounded-[1rem]">
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-full mt-4"></div>
          </div>
          <div className="w-[10rem] h-[8rem] bg-gray-200 rounded-[1.5rem]"></div>
        </div>
      </div>
    ));
  };

  // Determine the current active tab for back navigation
  const getCurrentTab = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const contentTypeParam = params.get("contentType") || "";
    // If we have a content type, use it as the previous tab for return navigation
    return contentTypeParam ? contentTypeParam.toLowerCase() : "forYou";
  }, [location.search]);

  const currentTab = getCurrentTab();

  return (
    <div className="flex flex-col h-full bg-[#FAFAFA]">
      {/* Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex gap-1.5 items-center px-3 py-4 text-base font-semibold tracking-tight text-center bg-white border-b border-solid border-b-zinc-200 text-[#1F2933]">
          <img
            loading="lazy"
            src={backIcon}
            className="object-contain shrink-0 self-stretch my-auto w-6 aspect-square"
            onClick={handleBackClick}
            alt="back"
          />
          <h1 className="flex items-center font-semibold text-[#1F2933] text-base leading-6 font-[Poppins]">
            {headerTitle}
          </h1>
        </div>
      </div>
      {title && (
        <div className="flex items-center gap-4 px-4 pt-4 pb-2">
          <h1 className="font-semibold text-xl text-[#1F2933] leading-7 font-[Poppins] flex items-center">
            {iconComponent && <span className="mr-2">{iconComponent}</span>}
            {title}
          </h1>
        </div>
      )}

      {/* Content list */}
      <div className="flex-1 overflow-auto pb-16">
        {isActionLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        )}
        <div className="px-4">
          {displayItems.length > 0 ? (
            <div className="space-y-4">
              {displayItems.map((item, index) => {
                // For the last item, attach the ref for intersection observer
                if (index === displayItems.length - 1) {
                  return (
                    <div
                      key={`${item.id}-${index}`}
                      ref={lastItemRef}
                      className="border-b border-[#E2E2EA]"
                    >
                      <TpRectangleCard
                        key={item.id}
                        isShowcontentType={true}
                        isLike={isItemLiked(item)}
                        isBookmarked={isItemBookmarked(item)}
                        onLike={() => handleLike(item, isItemLiked(item))}
                        onBookmark={() =>
                          handleBookmark(item, isItemBookmarked(item))
                        }
                        data={{
                          docId: item.docId,
                          contentTitle: item.contentTitle,
                          contentType: item.contentType,
                          uploadThumbnailUrl: item.uploadThumbnailUrl,
                          contentDescription: item.contentDescription,
                          previewDeepLink: item.previewDeepLink,
                          like_count:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.like_count || item.like_count,
                          contentTypeId: item.contentTypeId,
                          publishedDate: item.publishedDate,
                          isExternalSharable:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.isExternalSharable || 0,
                          previewUrl:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.previewUrl ||
                            item.previewDeepLink ||
                            "",
                          readTime:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.readTime || "",
                          duration:
                            contentCounts[`${item.docId}_${item.contentTypeId}`]
                              ?.duration || "",
                          currentTab: currentTab, // For tab preservation
                          source:
                            window.location.pathname + window.location.search, // Include complete URL with search params for back navigation
                          deepLinkCode: item.deepLinkCode,
                        }}
                        isBackground={false}
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={`${item.id}-${index}`}
                    className="border-b border-[#E2E2EA]"
                  >
                    <TpRectangleCard
                      key={item.id}
                      isLike={isItemLiked(item)}
                      isBookmarked={isItemBookmarked(item)}
                      onLike={() => handleLike(item, isItemLiked(item))}
                      onBookmark={() =>
                        handleBookmark(item, isItemBookmarked(item))
                      }
                      data={{
                        docId: item.docId,
                        contentTitle: item.contentTitle,
                        contentType: item.contentType,
                        uploadThumbnailUrl: item.uploadThumbnailUrl,
                        contentDescription: item.contentDescription,
                        previewDeepLink: item.previewDeepLink,
                        like_count:
                          contentCounts[`${item.docId}_${item.contentTypeId}`]
                            ?.like_count || item.like_count,
                        contentTypeId: item.contentTypeId,
                        publishedDate: item.publishedDate,
                        isExternalSharable:
                          contentCounts[`${item.docId}_${item.contentTypeId}`]
                            ?.isExternalSharable || 0,
                        previewUrl:
                          contentCounts[`${item.docId}_${item.contentTypeId}`]
                            ?.previewUrl ||
                          item.previewDeepLink ||
                          "",
                        readTime:
                          contentCounts[`${item.docId}_${item.contentTypeId}`]
                            ?.readTime || "",
                        duration:
                          contentCounts[`${item.docId}_${item.contentTypeId}`]
                            ?.duration || "",
                        currentTab: currentTab, // For tab preservation
                        source:
                          window.location.pathname + window.location.search, // Include complete URL with search params for back navigation
                        deepLinkCode: item.deepLinkCode,
                      }}
                      isBackground={false}
                    />
                  </div>
                );
              })}

              {/* Loading indicator for infinite scroll */}
              {(fetchExternally ? propLoading : loading) && (
                <div ref={loaderRef} className="py-4">
                  {renderSkeletons()}
                </div>
              )}
            </div>
          ) : (fetchExternally ? propLoading : loading) ? (
            <div className="space-y-4">{renderSkeletons()}</div>
          ) : (
            <div className="flex justify-center items-center py-8">
              <p className="text-gray-500">
                No content available in selected speciality
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentListSection;
